import { useQuery } from '@tanstack/react-query';
import type {
  InboundOverview,
  ItemDto,
  Page,
  PartnerDto,
  ReceiptDetailDto,
  ReceiptSummaryDto,
} from '@zenarty/shared';
import {
  AlertTriangle,
  CalendarClock,
  FileUp,
  Forklift,
  Plus,
  Timer,
  Trash2,
  Truck,
  Warehouse,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../api';
import { useAuth } from '../auth';
import { BarChart } from '../components/charts';
import { ImportDialog } from '../components/importer';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  PageHeader,
  Stat,
  Tabs,
  type Tone,
} from '../components/kit';
import { Dialog } from '../components/overlays';
import { ItemPicker } from '../components/pickers';
import { DataTable, type Column } from '../components/table';
import { formatDate, formatNumber, pct, timeAgo } from '../lib/format';
import { useAction, useClients, useSites } from '../lib/queries';

export const RECEIPT_TONE: Record<string, Tone> = {
  expected: 'neutral',
  arrived: 'info',
  receiving: 'accent',
  closed: 'ok',
  cancelled: 'danger',
};
const RECEIPT_LABEL: Record<string, string> = {
  expected: 'Expected',
  arrived: 'At dock',
  receiving: 'Receiving',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

export function ReceiptStatus({ status }: { status: string }) {
  return (
    <Badge tone={RECEIPT_TONE[status] ?? 'neutral'} dot>
      {RECEIPT_LABEL[status] ?? status}
    </Badge>
  );
}

export const minutesLabel = (m: number | null | undefined) =>
  m === null || m === undefined
    ? '—'
    : m < 60
      ? `${m} min`
      : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} min`;

export function Progress({ value, total }: { value: number; total: number }) {
  const p = total ? Math.min(100, Math.round((value / total) * 100)) : value ? 100 : 0;
  const over = total > 0 && value > total;
  return (
    <div className="stack-sm" style={{ gap: 4, minWidth: 110 }}>
      <div className="row small" style={{ justifyContent: 'space-between' }}>
        <span className="num">
          {formatNumber(value)} / {formatNumber(total)}
        </span>
        <span className={over ? 'warn-text' : 'muted'}>{total ? `${pct(value, total)}%` : ''}</span>
      </div>
      <div className="meter">
        <span style={{ width: `${p}%`, background: over ? 'var(--warn)' : undefined }} />
      </div>
    </div>
  );
}

type Tab = 'open' | 'expected' | 'arrived' | 'receiving' | 'closed' | 'all';

export function ReceiptsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const sites = useSites(can('site:read'));
  const clients = useClients();
  const [siteId, setSiteId] = useState('');
  const [tab, setTab] = useState<Tab>('open');
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const pageSize = 50;
  const params = new URLSearchParams({ limit: String(pageSize), offset: String(page * pageSize) });
  if (siteId) params.set('siteId', siteId);
  if (tab !== 'all') params.set('status', tab);
  if (search) params.set('q', search);
  const list = useQuery({
    queryKey: ['receipts', 'list', params.toString()],
    queryFn: () =>
      api<Page<ReceiptSummaryDto> & { counts: Record<string, number> }>(`/receipts?${params}`),
    refetchInterval: 30_000,
  });
  const overview = useQuery({
    queryKey: ['receipts', 'overview', siteId],
    queryFn: () => api<InboundOverview>(`/inbound/overview${siteId ? `?siteId=${siteId}` : ''}`),
    refetchInterval: 60_000,
  });
  const o = overview.data;
  const counts = list.data?.counts ?? {};
  const open = (counts.expected ?? 0) + (counts.arrived ?? 0) + (counts.receiving ?? 0);
  const multiClient = (clients.data?.items.length ?? 0) > 1;

  const columns: Column<ReceiptSummaryDto>[] = [
    {
      id: 'number',
      header: 'Receipt',
      cell: (r) => (
        <div>
          <div className="primary-cell mono">{r.number}</div>
          <div className="sub">
            {[
              r.type.toUpperCase(),
              r.poNumber ? (/^PO/i.test(r.poNumber) ? r.poNumber : `PO ${r.poNumber}`) : null,
              r.reference,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
      ),
    },
    ...(multiClient
      ? [{ id: 'client', header: 'Client', cell: (r: ReceiptSummaryDto) => r.clientName }]
      : []),
    {
      id: 'supplier',
      header: 'Supplier',
      cell: (r) => r.supplierName ?? <span className="faint">—</span>,
    },
    {
      id: 'expected',
      header: 'Expected',
      cell: (r) =>
        r.expectedDate ? (
          formatDate(`${r.expectedDate}T00:00:00`)
        ) : (
          <span className="faint">—</span>
        ),
    },
    { id: 'status', header: 'Status', cell: (r) => <ReceiptStatus status={r.status} /> },
    {
      id: 'door',
      header: 'Door',
      cell: (r) =>
        r.doorCode ? (
          <div>
            <span className="mono">{r.doorCode}</span>
            <div className="sub">{timeAgo(r.arrivedAt)}</div>
          </div>
        ) : (
          <span className="faint">—</span>
        ),
    },
    {
      id: 'progress',
      header: 'Received',
      cell: (r) => <Progress value={r.receivedUnits} total={r.expectedUnits} />,
    },
    {
      id: 'putaway',
      header: 'Putaway',
      align: 'right',
      cell: (r) =>
        r.openPutaway ? (
          <Badge tone="warn">{r.openPutaway} open</Badge>
        ) : r.stockedAt ? (
          <Badge tone="ok">Stocked</Badge>
        ) : (
          <span className="faint">—</span>
        ),
    },
  ];

  return (
    <div className="stack-lg">
      <PageHeader
        title="Receipts"
        description="Expected receipts, trailers at the dock and receiving progress. Dock-to-stock runs from check-in to the last putaway."
        actions={
          <>
            {can('site:read') && (sites.data?.items.length ?? 0) > 1 ? (
              <select
                className="select"
                aria-label="Site"
                value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
              >
                <option value="">All sites</option>
                {(sites.data?.items ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            ) : null}
            {can('inbound:submit') ? (
              <>
                <Button icon={FileUp} onClick={() => setImporting(true)}>
                  Import CSV
                </Button>
                <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                  New receipt
                </Button>
              </>
            ) : null}
          </>
        }
      />
      <div className="grid-stats">
        <Stat
          label="Expected today"
          icon={CalendarClock}
          value={o ? formatNumber(o.expectedToday) : '—'}
          foot={o ? `${formatNumber(counts.expected ?? 0)} not yet arrived` : null}
          onClick={() => setTab('expected')}
        />
        <Stat
          label="Trailers at dock"
          icon={Truck}
          tone={o?.atDock ? 'warn' : 'accent'}
          value={o ? formatNumber(o.atDock + o.receiving) : '—'}
          foot={o ? `${o.receiving} being received` : null}
          onClick={() => setTab('arrived')}
        />
        <Stat
          label="Units received today"
          icon={Warehouse}
          tone="ok"
          value={o ? formatNumber(o.unitsReceivedToday) : '—'}
          foot={o ? `${o.closedToday} receipts closed` : null}
        />
        <Stat
          label="Waiting for putaway"
          icon={Forklift}
          tone={o?.openPutaway ? 'warn' : 'ok'}
          value={o ? formatNumber(o.openPutaway) : '—'}
          foot="LPNs at the dock"
          onClick={() => void navigate('/inbound/putaway')}
        />
        <Stat
          label="Dock-to-stock (7 d avg)"
          icon={Timer}
          value={minutesLabel(o?.avgDockToStockMinutes)}
          foot={
            o?.pendingVariances
              ? `${o.pendingVariances} variance(s) need approval`
              : 'No variances waiting'
          }
          tone={o?.pendingVariances ? 'danger' : 'accent'}
        />
      </div>
      {o && o.dockToStockTrend.some((t) => t.receipts) ? (
        <Card title="Receipts closed per day" sub="Last 14 days, with average dock-to-stock time">
          <BarChart
            series={[{ key: 'receipts', label: 'Receipts closed', color: 'var(--accent)' }]}
            data={o.dockToStockTrend.map((t) => ({ label: t.date.slice(5), receipts: t.receipts }))}
            height={140}
          />
        </Card>
      ) : null}
      <div>
        <Tabs
          value={tab}
          onChange={(t) => {
            setTab(t);
            setPage(0);
          }}
          tabs={[
            { id: 'open', label: 'Open', count: open },
            { id: 'expected', label: 'Expected', count: counts.expected ?? 0 },
            { id: 'arrived', label: 'At dock', count: counts.arrived ?? 0 },
            { id: 'receiving', label: 'Receiving', count: counts.receiving ?? 0 },
            { id: 'closed', label: 'Closed', count: counts.closed ?? 0 },
            { id: 'all', label: 'All' },
          ]}
        />
        <DataTable
          columns={columns}
          rows={list.data?.items ?? []}
          rowKey={(r) => r.id}
          loading={list.isLoading}
          onRowClick={(r) => void navigate(`/inbound/receipts/${r.id}`)}
          searchPlaceholder="Search receipt, PO, reference or trailer"
          server={{
            total: list.data?.total ?? 0,
            page,
            pageSize,
            onPage: setPage,
            search,
            onSearch: (q) => {
              setSearch(q);
              setPage(0);
            },
          }}
          empty={
            <EmptyState icon={Truck} title="No receipts here">
              Create an expected receipt (ASN or PO), import a CSV, or start a blind receipt when a
              trailer turns up unannounced.
            </EmptyState>
          }
        />
      </div>
      {creating ? (
        <CreateReceiptDialog
          onClose={() => setCreating(false)}
          onCreated={(id) => void navigate(`/inbound/receipts/${id}`)}
        />
      ) : null}
      {importing ? <ImportReceipts onClose={() => setImporting(false)} /> : null}
    </div>
  );
}

function ImportReceipts({ onClose }: { onClose: () => void }) {
  const { me } = useAuth();
  const sites = useSites();
  const clients = useClients();
  const [siteId, setSiteId] = useState('');
  const [clientId, setClientId] = useState(me?.clientId ?? '');
  return (
    <ImportDialog
      title="Import expected receipts"
      description="One row per line; rows with the same receipt_number become one receipt. Quantities may be in any pack level (uom)."
      endpoint="/receipts/import"
      scope={{ siteId, clientId }}
      scopeReady={Boolean(siteId && clientId)}
      picker={
        <div className="form-grid">
          <Field label="Site">
            <select className="select" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
              <option value="">Choose…</option>
              {(sites.data?.items ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Client">
            <select
              className="select"
              value={clientId}
              disabled={Boolean(me?.clientId)}
              onChange={(e) => setClientId(e.target.value)}
            >
              <option value="">Choose…</option>
              {(clients.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
      }
      templateHeaders={[
        'receipt_number',
        'sku',
        'qty',
        'uom',
        'po_number',
        'po_line',
        'lot',
        'expiry',
        'sscc',
        'expected_date',
        'supplier',
        'reference',
      ]}
      templateExample={[
        'ASN-20031',
        'SKU-100',
        4,
        'CS',
        'PO-5512',
        '1',
        'L2401',
        '2027-06-30',
        '',
        '2026-11-02',
        '',
        '',
      ]}
      templateName="receipts-template.csv"
      invalidate="receipts"
      onClose={onClose}
    />
  );
}

interface DraftLine {
  key: number;
  item: ItemDto | null;
  qty: string;
  uom: string;
  lot: string;
  expiry: string;
  sscc: string;
}

function CreateReceiptDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { me } = useAuth();
  const sites = useSites();
  const clients = useClients();
  const [siteId, setSiteId] = useState('');
  const [clientId, setClientId] = useState(me?.clientId ?? '');
  const [type, setType] = useState<'asn' | 'po' | 'blind'>('asn');
  const [number, setNumber] = useState('');
  const [po, setPo] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [expectedDate, setExpectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [carrier, setCarrier] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([
    { key: 1, item: null, qty: '', uom: '', lot: '', expiry: '', sscc: '' },
  ]);
  const suppliers = useQuery({
    queryKey: ['partners', 'supplier'],
    queryFn: () => api<Page<PartnerDto>>('/trading-partners?type=supplier&limit=200'),
    enabled: !me?.clientId,
  });
  const siteList = (sites.data?.items ?? []).filter(
    (s) =>
      !clientId ||
      (clients.data?.items.find((c) => c.id === clientId)?.siteIds ?? [s.id]).includes(s.id),
  );
  const set = (key: number, patch: Partial<DraftLine>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const create = useAction(
    () =>
      api<ReceiptDetailDto>('/receipts', {
        method: 'POST',
        body: {
          siteId,
          clientId,
          number: number.trim(),
          type,
          poNumber: po || null,
          supplierId: supplierId || null,
          expectedDate: expectedDate || null,
          carrier: carrier || null,
          lines:
            type === 'blind'
              ? []
              : lines
                  .filter((l) => l.item && Number(l.qty))
                  .map((l) => ({
                    itemId: l.item!.id,
                    qty: Number(l.qty),
                    uom: l.uom || undefined,
                    lot: l.lot || null,
                    expiry: l.expiry || null,
                    sscc: l.sscc || null,
                  })),
        },
      }),
    {
      invalidate: [['receipts']],
      success: (r) => `Receipt ${r.number} created`,
      onSuccess: (r) => {
        onClose();
        onCreated(r.id);
      },
    },
  );
  const validLines = lines.filter((l) => l.item && Number(l.qty) > 0).length;
  return (
    <Dialog
      wide
      title="New receipt"
      description="An ASN or purchase order the warehouse should expect. Blind receipts have no lines: everything is captured at the dock."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={!siteId || !clientId || !number.trim() || (type !== 'blind' && !validLines)}
            onClick={() => create.mutate(undefined)}
          >
            Create receipt
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="form-grid-3">
          <Field label="Client">
            <select
              className="select"
              value={clientId}
              disabled={Boolean(me?.clientId)}
              onChange={(e) => setClientId(e.target.value)}
            >
              <option value="">Choose…</option>
              {(clients.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Site">
            <select className="select" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
              <option value="">Choose…</option>
              {siteList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Type">
            <div className="segmented">
              {(['asn', 'po', 'blind'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  className={type === t ? 'active' : ''}
                  onClick={() => setType(t)}
                >
                  {t === 'asn' ? 'ASN' : t === 'po' ? 'PO' : 'Blind'}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Receipt number">
            <input
              className="input mono"
              value={number}
              placeholder="ASN-20031"
              onChange={(e) => setNumber(e.target.value)}
            />
          </Field>
          <Field label="PO number">
            <input className="input" value={po} onChange={(e) => setPo(e.target.value)} />
          </Field>
          <Field label="Expected date">
            <input
              className="input"
              type="date"
              value={expectedDate}
              onChange={(e) => setExpectedDate(e.target.value)}
            />
          </Field>
          {!me?.clientId ? (
            <Field label="Supplier">
              <select
                className="select"
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
              >
                <option value="">—</option>
                {(suppliers.data?.items ?? [])
                  .filter((s) => !s.clientId || s.clientId === clientId)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
              </select>
            </Field>
          ) : null}
          <Field label="Carrier">
            <input className="input" value={carrier} onChange={(e) => setCarrier(e.target.value)} />
          </Field>
        </div>
        {type !== 'blind' ? (
          <div className="stack-sm">
            <div className="section-title">Lines</div>
            {!clientId ? <div className="muted small">Choose the client first.</div> : null}
            {lines.map((l, i) => (
              <div
                key={l.key}
                className="row"
                style={{ alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}
              >
                <span className="muted small num" style={{ width: 20, paddingTop: 10 }}>
                  {i + 1}
                </span>
                <div style={{ flex: '2 1 220px' }}>
                  <ItemPicker
                    value={l.item}
                    clientId={clientId || undefined}
                    onChange={(item) => set(l.key, { item, uom: item?.baseUom ?? '' })}
                  />
                </div>
                <input
                  className="input num"
                  style={{ width: 80 }}
                  placeholder="Qty"
                  inputMode="numeric"
                  value={l.qty}
                  onChange={(e) => set(l.key, { qty: e.target.value })}
                />
                <select
                  className="select"
                  style={{ width: 80 }}
                  value={l.uom}
                  onChange={(e) => set(l.key, { uom: e.target.value })}
                  aria-label="Unit"
                >
                  {(l.item?.uoms ?? [{ uom: 'EA' }]).map((u) => (
                    <option key={u.uom} value={u.uom}>
                      {u.uom}
                    </option>
                  ))}
                </select>
                <input
                  className="input"
                  style={{ width: 110 }}
                  placeholder="Lot"
                  value={l.lot}
                  onChange={(e) => set(l.key, { lot: e.target.value })}
                />
                <input
                  className="input"
                  style={{ width: 150 }}
                  type="date"
                  aria-label="Expiry"
                  value={l.expiry}
                  onChange={(e) => set(l.key, { expiry: e.target.value })}
                />
                <input
                  className="input mono"
                  style={{ width: 190 }}
                  placeholder="Pallet SSCC (optional)"
                  value={l.sscc}
                  onChange={(e) => set(l.key, { sscc: e.target.value.replace(/\D/g, '') })}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  icon={Trash2}
                  aria-label="Remove line"
                  disabled={lines.length === 1}
                  onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                />
              </div>
            ))}
            <div>
              <Button
                size="sm"
                icon={Plus}
                onClick={() =>
                  setLines((ls) => [
                    ...ls,
                    {
                      key: Date.now(),
                      item: null,
                      qty: '',
                      uom: '',
                      lot: '',
                      expiry: '',
                      sscc: '',
                    },
                  ])
                }
              >
                Add line
              </Button>
            </div>
          </div>
        ) : (
          <div className="callout callout-info">
            <AlertTriangle aria-hidden />
            <div>
              Blind receipt: the dock team scans whatever arrives. Every line is recorded as
              received, with no expected quantity.
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
